import React, { useState, useEffect } from 'react';
import Div from '@/shared/Div';
import Button from '@/shared/Button';
import Dropdown from '@/shared/crisp/Dropdown';
import LocationsContainer from '@/components/StoreLocator/LocationsContainer';
import Heading from '@/shared/Heading';

interface State {
  id: string;
  label: string;
}

interface LocationSelectorProps {
  states: State[];
  cities: State[];
  selectedState: State | null;
  showLocations: boolean;
  locations: any[];
  isMobile: boolean;
  categoryId: string;
  onStateChange: (state: State) => void;
  onCityChange: (city: State) => void;
  onReset: () => void;
  onSubmit: () => void;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  states,
  cities,
  selectedState,
  categoryId,
  showLocations,
  locations,
  isMobile,
  onStateChange,
  onCityChange,
  onReset,
  onSubmit,
}) => {
  const [stateKey, setStateKey] = useState(0);
  const [cityKey, setCityKey] = useState(0);

  const isDefaultSelection = selectedState?.id === 'all' && cities[0]?.id === 'all';

  // Update cityKey whenever cities array changes
  useEffect(() => {
    setCityKey((prev) => prev + 1);
  }, [cities]);

  const handleReset = () => {
    setStateKey((prev) => prev + 1);
    setCityKey((prev) => prev + 1);
    onReset();
  };

  const renderSelectors = () => (
    <Div className="flex flex-col self-stretch items-stretch gap-s5">
      <Dropdown
        key={`state-${stateKey}`}
        name="state"
        dropdownList={states}
        onChange={onStateChange}
      />
      <Dropdown key={`city-${cityKey}`} name="city" dropdownList={cities} onChange={onCityChange} />
    </Div>
  );

  const renderButtons = () => (
    <Div className="flex justify-between items-stretch gap-s5 mt-s5 w-full">
      <Button
        theme="secondary"
        size="base"
        onClick={handleReset}
        className="flex-1"
        disabled={isDefaultSelection}
      >
        Reset
      </Button>
      <Button
        theme="primary"
        size="base"
        onClick={onSubmit}
        className="flex-1"
        disabled={isDefaultSelection}
      >
        Submit
      </Button>
    </Div>
  );

  if (!isMobile) {
    return (
      <Div className="inline-flex items-start gap-s7 mb-s7">
        <Div className="flex flex-col items-start w-[374px] gap-s6">
          <Heading level={3} className="text-h-3 font-bold">
            Select State and City
          </Heading>
          {renderSelectors()}
          {renderButtons()}
        </Div>
        <LocationsContainer visible={showLocations} locations={locations} categoryId={categoryId} />
      </Div>
    );
  }

  return (
    <Div className="flex flex-col gap-s7 mb-s7">
      <Div className="flex flex-col items-start w-full gap-s6">
        <Heading level={3} className="text-h-3 font-bold">
          Select State and City
        </Heading>
        {renderSelectors()}
        {renderButtons()}
      </Div>
      <LocationsContainer
        visible={showLocations}
        locations={locations}
        isMobile={true}
        categoryId={categoryId}
      />
    </Div>
  );
};

export default LocationSelector;
